import React, { useState } from 'react';

import { OrderCell } from './OrderCell';
import { formatDateToMainUTC, getAvailableOrderUsers, getChangedOrderFields, getFullDateStringByStamp, getLocalUTCDate, getLocalUTCDateTimestamp, getLocalUTCTime, validateOrderFields } from '../../helpers';
import { FlagInput } from '../../components/Input/FlagInput';
import { Select } from '../../components/Select';
import { Input } from '../../components/Input';
import { AddressInput } from '../../components/Input/AddressInput';
import { Textarea } from '../../components/Textarea';
import { useDispatch, useSelector } from 'react-redux';
import { PhoneInput } from '../../components/Input/PhoneInput';
import { Button } from '../../components/Button';
import { CLIENT_NOTS, getClientNotify } from '../../redux/notifySlice';
import { editOrder } from '../../redux/orderSlice';
import { orderStatuses } from '../../names';

export const EditableOrder = ({ titles, gridTemplateColumns, data, closeEditor }) => {
    const advertTypes = useSelector((state) => state.settings.advertTypes);
    const orderTypes = useSelector((state) => state.settings.orderTypes);
    const cities = useSelector((state) => state.settings.orderCities);
    const profiles = useSelector((state) => state.settings.orderProfiles);

    const emptyWorker = {fullname: 'Нет мастера', _id: ''};

    const botTelegramId = useSelector((state) => state.settings.botTelegramId);
    const orderUserIds = useSelector((state) => state.settings.orderUsers);
    const orderUsers = useSelector((state) => state.users.list).filter(u => orderUserIds.some(u2 => u2 === u._id));
    const availableOrderUsers = [emptyWorker, ...getAvailableOrderUsers(orderUsers, data.city, data.profileType, botTelegramId)];
    
    const orderDate = getLocalUTCDate(data.city.UTC, data.date.min, true);
    const completionDate = data.completionDate ? getFullDateStringByStamp(data.city.UTC, data.completionDate) : '';
    const orderTime = getLocalUTCTime(data.city.UTC, data.date, true);
    const clientPhoneAccess = titles.find((title) => title.key === 'clientPhone');
    const otherCitiesAccess = titles.find((title) => title.key === 'city');
    const otherProfilesAccess = titles.find((title) => title.key === 'profileType');

    const dispatch = useDispatch();

    const addressObj = {
        village: data.address.village,
        street: data.address.street,
        apartmentNumber: data.address.apartmentNumber,
        additional: data.address.additional
    };

    const defaultState = {
        advertType: data.advertType._id,
        sourceType: data.sourceType,
        orderType: data.orderType._id,
        clientName: data.clientName,
        orderComment: data.orderComment,
        city: data.city._id,
        address: addressObj,
        date: orderDate,
        time: String(orderTime),
        clientPhone: data.clientPhone || '',
        profileType: data.profileType._id,
        isProfile: data.isProfile,
        worker: data.worker?._id || emptyWorker._id,
        totalAmount: data.totalAmount || data.totalAmount === 0 ? data.totalAmount : '',
        partsCost: data.partsCost || data.partsCost === 0 ? data.partsCost : '',
        completionDate: completionDate,
        workerComment: data.workerComment || '',
    }

    const [orderFields, setOrderFields] = useState(defaultState);

    const changeOrderFieldHandler = (fieldName, value) => {
        setOrderFields(prevValues => ({
            ...prevValues,
            [fieldName]: value
        }));
    };

    const handlers = [
        (e) => changeOrderFieldHandler('advertType', e.target.value),
        (e) => changeOrderFieldHandler('sourceType', e.target.value),
        (e) => changeOrderFieldHandler('orderType', e.target.value),
        (e) => changeOrderFieldHandler('clientName', e.target.value),
        (e) => changeOrderFieldHandler('orderComment', e.target.value),
        (e) => changeOrderFieldHandler('city', e.target.value),
        (value) => changeOrderFieldHandler('address', value),
        (e) => changeOrderFieldHandler('date', e.target.value),
        (e) => changeOrderFieldHandler('time', e.target.value),
        (e) => changeOrderFieldHandler('clientPhone', e.target.value),
        (e) => changeOrderFieldHandler('profileType', e.target.value),
        (value) => changeOrderFieldHandler('isProfile', value),
        (e) => changeOrderFieldHandler('worker', e.target.value),
        (e) => changeOrderFieldHandler('totalAmount', e.target.value),
        (e) => changeOrderFieldHandler('partsCost', e.target.value),
        (e) => changeOrderFieldHandler('completionDate', e.target.value),
        (e) => changeOrderFieldHandler('workerComment', e.target.value),
    ];

    const isOrderFinished = data.details.finished;
    const isCancelStatus = data.orderStatus.name === orderStatuses.CANCEL;
    const isModernStatus = data.orderStatus.name === orderStatuses.MODERN;
    const isProcessOrder = (data.orderStatus.name !== orderStatuses.NOT_ASSIGNED && data.orderStatus.name !== orderStatuses.MODERN) && !isOrderFinished;
    const saveOrderHandler = () => {
        const result = validateOrderFields(orderFields, titles);
        
        if (!result.isOkay) {
            dispatch(getClientNotify({
                data: result,
                action: CLIENT_NOTS.EMPTY_FIELD,
                }),
            );
            return;
        }
        const cityUTC = cities.find(c => c._id === orderFields.city).UTC;
        const time = formatDateToMainUTC(orderFields.date, orderFields.time, cityUTC);
        const completionDate = orderFields.completionDate ?
            getLocalUTCDateTimestamp(cityUTC, orderFields.completionDate)
            : null;
        const formattedData = {
            _id: data._id,
            ...orderFields,
            date: {
                min: time.min,
                max: time.max
            },
            worker: orderFields.worker || null,
            totalAmount: orderFields.totalAmount ? +orderFields.totalAmount : data.totalAmount,
            partsCost: orderFields.partsCost ? +orderFields.partsCost : data.partsCost,
            completionDate,
            workerComment: orderFields.workerComment || data.workerComment,
        }
        delete formattedData.time;
        const changedFields = getChangedOrderFields(data, formattedData);
        if (!Object.keys(changedFields).length) {
            dispatch(getClientNotify({
                data: {isOkay: false},
                action: CLIENT_NOTS.NO_ORDER_CHANGES,
                }),
            );
            return;
        }
        
        const notifyWorker = data.worker ? window.confirm('Оповестить мастера об изменении заявки?\n(Это касается только содержания заявки)') : false;
        
        const requestBody = {
            orderData: {...changedFields, _id: data._id},
            notifyWorker,
            closeEditor,
        }
        dispatch(editOrder(requestBody));
    }

    return (
        <div className='edit-order'>
            <div className="edit-order__body" style={{gridTemplateColumns}}>
            <OrderCell value={data.id} />
            <Select
                className="cell"
                enums={advertTypes}
                value={orderFields.advertType}
                changeHandler={handlers[0]}
            />
            <Input
                className="cell"
                placeholder="Представиться"
                value={orderFields.sourceType}
                changeHandler={handlers[1]}
            />
            <Select
                className="cell"
                enums={orderTypes}
                value={orderFields.orderType}
                changeHandler={handlers[2]}
            />
            <Input
                className="cell"
                placeholder="Имя клиента"
                value={orderFields.clientName}
                changeHandler={handlers[3]}
            />
            <Textarea className="cell dark-theme-bg0" value={orderFields.orderComment} changeHandler={handlers[4]} />
            {otherCitiesAccess && (
                <Select
                    className="cell"
                    enums={cities}
                    value={orderFields.city}
                    changeHandler={handlers[5]}
                />
            )}
            <AddressInput className="cell" value={orderFields.address} changeHandler={handlers[6]} />
            <Input className="cell" type="date" value={orderFields.date} changeHandler={handlers[7]} />
            <Input className='cell'
                    value={orderFields.time}
                    centered={true}
                    placeholder='h-h или h:m-h:m'
                    changeHandler={handlers[8]}
                />
            {clientPhoneAccess && (
                <PhoneInput className="cell" value={orderFields.clientPhone} changeHandler={handlers[9]} />
            )}
            {otherProfilesAccess && (
                <Select
                    className="cell"
                    enums={profiles}
                    value={orderFields.profileType}
                    changeHandler={handlers[10]}
                />
            )}
            <FlagInput
                className="cell"
                values={['Профиль', 'Непрофиль']}
                flag={orderFields.isProfile}
                changeHandler={handlers[11]}
            />
            <OrderCell value={data.orderStatus.name} />
            <Select
                className="cell"
                enums={availableOrderUsers}
                value={orderFields.worker}
                disabled={(!isOrderFinished && !isModernStatus) || isProcessOrder}
                changeHandler={handlers[12]}
            />
            <Input
                className="cell"
                placeholder="Общая"
                value={orderFields.totalAmount}
                disabled={!isOrderFinished || isCancelStatus}
                changeHandler={handlers[13]}
            />
            <Input
                className="cell"
                placeholder="ЗПЧ"
                value={orderFields.partsCost}
                disabled={!isOrderFinished || isCancelStatus}
                changeHandler={handlers[14]}
            />
            <Input
                className="cell"
                value={data.clearAmount || ''}
                readonly={true} disabled={true}
            />
             <Input
                className="cell"
                value={data.handOver || ''}
                readonly={true} disabled={true}
            />
            <Input className="cell" type="datetime-local" value={orderFields.completionDate} disabled={!isOrderFinished} changeHandler={handlers[15]}/>
            <Textarea className="cell dark-theme-bg0" value={orderFields.workerComment} disabled={!isOrderFinished} changeHandler={handlers[16]} />
            </div>
            <div className="add-order__buttons">
                <Button size='small' onClick={saveOrderHandler} text='сохранить'/>
                <Button size='small' onClick={closeEditor} text='отмена'/>
            </div>
        </div>
    );
};
